import React, { useState, useMemo, useContext, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Dropdown } from 'react-bootstrap';
import {
  FaUsers, FaUserCheck, FaUserTimes, FaUserPlus,
  FaEllipsisV, FaEye, FaBan, FaUnlock,
  FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendarAlt, FaIdBadge, FaTimes
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../../../assets/css/superadmin/usersmangment/endusersmanagment/EndUsersManagment.css';
import MasterLayout from '../../../../layout/superadmin/MasterLayout';
import axios from "axios";
import AppContext from '../../../../context/AppContext';

// ── Helper ──
const getInitials = (name) =>
  (name || '')
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

// ── Customer Detail Modal (mechanic-management style) ──
const CustomerDetailModal = ({ user, onClose, baseUrlForFile }) => {
  if (!user) return null;

  const isActive = (user.status || '').toLowerCase() === 'active';

  const infoFields = [
    { icon: <FaEnvelope />, label: 'Email', value: user.email },
    { icon: <FaPhone />, label: 'Phone', value: user.phonenumber },
    { icon: <FaMapMarkerAlt />, label: 'Address', value: user.address },
    { icon: <FaIdBadge />, label: 'Customer ID', value: user.id },
    { icon: <FaCalendarAlt />, label: 'Join Date', value: user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A' },
  ];

  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal-box" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="um-modal-header">
          <div>
            <h2 className="um-modal-title">Customer Details</h2>
            <p className="um-modal-subtitle">Full profile information</p>
          </div>
          <button className="um-modal-close" onClick={onClose}>
            <FaTimes size={16} />
          </button>
        </div>

        {/* Profile strip */}
        <div className="um-modal-profile">
          <div className="um-modal-avatar">
            {user.image
              ? <img src={`${baseUrlForFile}/${user.image}`} alt={user.fullname} onError={(e) => { e.target.style.display = 'none'; }} />
              : getInitials(user.fullname)
            }
          </div>
          <div className="um-modal-meta">
            <h3>{user.fullname}</h3>
            <p>{user.email}</p>
            <span className={`um-status-badge ${isActive ? 'um-status-active' : 'um-status-inactive'}`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Info grid */}
        <div className="um-modal-info-grid">
          {infoFields.map((field, i) => (
            <div className="um-modal-info-item" key={i}>
              <span className="um-modal-info-icon">{field.icon}</span>
              <div>
                <span className="um-modal-info-label">{field.label}</span>
                <span className="um-modal-info-value">{field.value || '—'}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="um-modal-actions">
          <button className="um-btn-cancel" onClick={onClose}>Close</button>
        </div>

      </div>
    </div>
  );
};

const EndUsersManagment = () => {
  const { baseUrl, authToken, baseUrlForFile } = useContext(AppContext);
  const [errorMessage, setErrorMessage] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const viewAllCustomers = () => {
    axios.get(`${baseUrl}/superadmin/customer/viewAllCustomers`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((response) => {
        if (response.data.success == true) {
          setUsers(response.data.customers);
        } else if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load customers.",
        });
      });
  };

  const handleToggleStatus = (userId) => {
    const formData = new FormData();
    formData.append("id", userId);
    axios.post(`${baseUrl}/superadmin/customer/updateStatus`, formData, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      }
    })
      .then((response) => {
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "User status updated successfully!",
          });
          viewAllCustomers();
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update user status.",
        });
      });
  };

  useEffect(() => {
    viewAllCustomers();
  }, []);

  // Calculate dynamic stats — customer-only
  const stats = useMemo(() => {
    const totalCustomers   = users.length;
    const activeCustomers  = users.filter(u => (u.status || '').toLowerCase() === 'active').length;
    const blockedCustomers = users.filter(u => (u.status || '').toLowerCase() === 'inactive').length;

    const now = new Date();
    const newThisMonth = users.filter(u => {
      if (!u.created_at) return false;
      const d = new Date(u.created_at);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;

    return [
      {
        icon: <FaUsers />,
        label: 'TOTAL CUSTOMERS',
        value: totalCustomers.toString(),
        color: '#FDB714',
        bgColor: '#FFF8E6'
      },
      {
        icon: <FaUserCheck />,
        label: 'ACTIVE CUSTOMERS',
        value: activeCustomers.toString(),
        color: '#4CAF50',
        bgColor: '#E8F5E9'
      },
      {
        icon: <FaUserTimes />,
        label: 'BLOCKED CUSTOMERS',
        value: blockedCustomers.toString(),
        color: '#F44336',
        bgColor: '#FFEBEE'
      },
      {
        icon: <FaUserPlus />,
        label: 'NEW THIS MONTH',
        value: newThisMonth.toString(),
        color: '#2196F3',
        bgColor: '#E3F2FD'
      }
    ];
  }, [users]);

  // View user details
  const handleViewUser = (user) => {
    setSelectedUser(user);
  };

  // Block user
  const handleBlockUser = (user) => {
    Swal.fire({
      title: 'Block User?',
      text: `Are you sure you want to block ${user.fullname}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#F44336',
      cancelButtonColor: '#999',
      confirmButtonText: 'Yes, block user',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        handleToggleStatus(user.id);
      }
    });
  };

  // Unblock user
  const handleUnblockUser = (user) => {
    Swal.fire({
      title: 'Unblock User?',
      text: `Are you sure you want to unblock ${user.fullname}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4CAF50',
      cancelButtonColor: '#999',
      confirmButtonText: 'Yes, unblock user',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        handleToggleStatus(user.id);
      }
    });
  };

  return (
    <MasterLayout>
      <div className="um-wrapper">
        <Container fluid className="um-container">
          {/* Header */}
          <div className="um-header">
            <div>
              <h2 className="um-title">Customer Management</h2>
              <p className="um-subtitle">Manage customers and their account status.</p>
            </div>
          </div>

          {/* Stats Cards */}
          <Row className="um-stats-row">
            {stats.map((stat, index) => (
              <Col key={index} xs={12} sm={6} lg={3} className="mb-3">
                <Card className="um-stat-card">
                  <Card.Body>
                    <div
                      className="um-stat-icon"
                      style={{ backgroundColor: stat.bgColor, color: stat.color }}
                    >
                      {stat.icon}
                    </div>
                    <div className="um-stat-info">
                      <h3 className="um-stat-value">{stat.value}</h3>
                      <span className="um-stat-label">{stat.label}</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Users Table */}
          <Card className="um-main-card">
            <Card.Body>
              <div className="um-table-wrapper">
                <Table responsive className="um-table">
                  <thead>
                    <tr>
                      <th>CUSTOMER</th>
                      <th>PHONE NUMBER</th>
                      <th>JOIN DATE</th>
                      <th>STATUS</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length > 0 ? users.map((user) => {
                      const isActive = (user.status || '').toLowerCase() === 'active';
                      return (
                        <tr key={user.id}>
                          <td>
                            <div className="um-user-identity">
                              <div className="um-user-avatar">
                                {user.image
                                  ? <img src={`${baseUrlForFile}/${user.image}`} alt={user.fullname} onError={(e) => { e.target.style.display = 'none'; }} />
                                  : getInitials(user.fullname)
                                }
                              </div>
                              <div className="um-user-info">
                                <div className="um-user-name">{user.fullname}</div>
                                <div className="um-user-email-sub">{user.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="um-user-phone">{user.phonenumber || "N/A"}</td>
                          <td className="um-user-date">{user.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}</td>
                          <td>
                            <span className={`um-status-badge ${isActive ? 'um-status-active' : 'um-status-inactive'}`}>
                              {isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                variant="link"
                                className="um-actions-toggle"
                                id={`dropdown-${user.id}`}
                              >
                                <FaEllipsisV />
                              </Dropdown.Toggle>

                              <Dropdown.Menu className="um-dropdown-menu">
                                <Dropdown.Item
                                  onClick={() => handleViewUser(user)}
                                  className="um-dropdown-item"
                                >
                                  <FaEye className="um-dropdown-icon" /> View Details
                                </Dropdown.Item>
                                {isActive ? (
                                  <Dropdown.Item
                                    onClick={() => handleBlockUser(user)}
                                    className="um-dropdown-item um-dropdown-danger"
                                  >
                                    <FaBan className="um-dropdown-icon" /> Block User
                                  </Dropdown.Item>
                                ) : (
                                  <Dropdown.Item
                                    onClick={() => handleUnblockUser(user)}
                                    className="um-dropdown-item um-dropdown-success"
                                  >
                                    <FaUnlock className="um-dropdown-icon" /> Unblock User
                                  </Dropdown.Item>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan="5" className="text-center py-5 text-muted">
                          No customers found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="um-pagination">
                <span className="um-pagination-info">
                  Showing {users.length} to {users.length} of {users.length} results
                </span>
                <div className="um-pagination-controls">
                  <button className="um-pagination-btn" disabled>&lt;</button>
                  <button className="um-pagination-btn um-pagination-active">1</button>
                  <button className="um-pagination-btn" disabled>&gt;</button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>

      {/* Detail Modal */}
      {selectedUser && (
        <CustomerDetailModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          baseUrlForFile={baseUrlForFile}
        />
      )}
    </MasterLayout>
  );
};

export default EndUsersManagment;